import { FACES } from "./types";
// Hand-derived from 3D cube-corner positions; verified by the symmetry test.
const CROSSINGS = {
    F: {
        top: { face: "T", map: (i, N) => ({ r: N - 1, c: i }) },
        right: { face: "R", map: (i) => ({ r: i, c: 0 }) },
        bottom: { face: "D", map: (i) => ({ r: 0, c: i }) },
        left: { face: "L", map: (i, N) => ({ r: i, c: N - 1 }) },
    },
    R: {
        top: { face: "T", map: (i, N) => ({ r: N - 1 - i, c: N - 1 }) },
        right: { face: "B", map: (i) => ({ r: i, c: 0 }) },
        bottom: { face: "D", map: (i, N) => ({ r: i, c: N - 1 }) },
        left: { face: "F", map: (i, N) => ({ r: i, c: N - 1 }) },
    },
    L: {
        top: { face: "T", map: (i) => ({ r: i, c: 0 }) },
        right: { face: "F", map: (i) => ({ r: i, c: 0 }) },
        bottom: { face: "D", map: (i, N) => ({ r: N - 1 - i, c: 0 }) },
        left: { face: "B", map: (i, N) => ({ r: i, c: N - 1 }) },
    },
    B: {
        top: { face: "T", map: (i, N) => ({ r: 0, c: N - 1 - i }) },
        right: { face: "L", map: (i) => ({ r: i, c: 0 }) },
        bottom: { face: "D", map: (i, N) => ({ r: N - 1, c: N - 1 - i }) },
        left: { face: "R", map: (i, N) => ({ r: i, c: N - 1 }) },
    },
    T: {
        top: { face: "B", map: (i, N) => ({ r: 0, c: N - 1 - i }) },
        right: { face: "R", map: (i, N) => ({ r: 0, c: N - 1 - i }) },
        bottom: { face: "F", map: (i) => ({ r: 0, c: i }) },
        left: { face: "L", map: (i) => ({ r: 0, c: i }) },
    },
    D: {
        top: { face: "F", map: (i, N) => ({ r: N - 1, c: i }) },
        right: { face: "R", map: (i, N) => ({ r: N - 1, c: i }) },
        bottom: { face: "B", map: (i, N) => ({ r: N - 1, c: N - 1 - i }) },
        left: { face: "L", map: (i, N) => ({ r: N - 1, c: N - 1 - i }) },
    },
};
const OFFSETS = [
    [-1, -1], [-1, 0], [-1, 1],
    [0, -1], [0, 1],
    [1, -1], [1, 0], [1, 1],
];
// Returns all neighbors of (face, r, c) on the cube surface. Length is 7 at cube-corner cells, 8 elsewhere.
// The 8th candidate (diagonal off both edges) at a cube corner is dropped, since the cube corner is a
// 3-way meeting point with no fourth cell.
export function getNeighbors(face, r, c, N) {
    const result = [];
    for (const [dr, dc] of OFFSETS) {
        const nr = r + dr;
        const nc = c + dc;
        const outR = nr < 0 || nr >= N;
        const outC = nc < 0 || nc >= N;
        if (!outR && !outC) {
            result.push({ face, r: nr, c: nc });
            continue;
        }
        if (outR && outC)
            continue; // cube corner: no diagonal neighbor exists
        const edge = outR ? (nr < 0 ? "top" : "bottom") : (nc < 0 ? "left" : "right");
        // Index along the edge is whichever coord stayed in bounds.
        const idx = outR ? nc : nr;
        const crossing = CROSSINGS[face][edge];
        const mapped = crossing.map(idx, N);
        result.push({ face: crossing.face, r: mapped.r, c: mapped.c });
    }
    return result;
}
export function createEmptyCube(N) {
    const make = () => Array.from({ length: N }, () => Array.from({ length: N }, () => ({
        isMine: false,
        isRevealed: false,
        isFlagged: false,
        adjacentMines: 0,
    })));
    return { F: make(), B: make(), L: make(), R: make(), T: make(), D: make() };
}
export function totalCells(N) {
    return 6 * N * N;
}
export function forEachCell(cube, fn) {
    for (const face of FACES) {
        const grid = cube[face];
        const N = grid.length;
        for (let r = 0; r < N; r++) {
            for (let c = 0; c < N; c++) {
                fn(grid[r][c], { face, r, c });
            }
        }
    }
}
